import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Alert } from 'antd';
import * as actions from '@actions';
import useIsRestricted from '@hooks/useIsRestricted';
import { css } from '@emotion/react';
import useAuth from '@vl-core/useAuth';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import SelfBookAndF2fReferralFields from '@components/Clinicians/Session/v1/Components/SelfBookAndF2fReferralFields';
import F2fCompletedFields from '@components/Clinicians/Session/v1/Components/F2fCompletedFields';
import PIFUFields from '@components/Clinicians/Session/v1/Components/PIFUFields';
import { RecommendationsFormItem } from '@components/Clinicians/Session/Components/RecommendationsFormItem';
import { AppointmentOutcomeFormItem } from '@components/Clinicians/Session/Components/AppointmentOutcomeFormItem';
import { PriorityFormItem } from '@components/Clinicians/Session/v1/Components/PriorityFormItem';
import { ReferralToTreatmentFormItem } from '@components/Clinicians/Session/v1/Components/ReferralToTreatmentFormItem';
import { LetterTypesFormItem } from '@components/Clinicians/Session/Components/LetterTypesFormItem';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useApptReferral from '@components/Clinicians/Session/hooks/useApptReferral';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';

const DiagnosisAndOutcome = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const appointment_id = useSessionAppointmentId();

  const { appointment, refetchAppointment: onOutcomeSaved } = useSessionAppointment();
  const isCompleted = !!appointment.appointment_completed;

  const [rtt] = useSessionFormState('Referral To Treatment', appointment.appt_rtt_no || undefined);
  const [priority] = useSessionFormState('Priority', appointment.appt_pri_no || undefined);
  const [appointmentOutcome, setAppointmentOutcome] = useSessionFormState(
    'appointment_outcome',
    appointment.appointment_outcome_code || undefined
  );
  const [postedReferralStartDate, setPostedReferralStartDate] = useSessionFormState(
    'postedReferralStartDate',
    appointment.auth_granted_start_date || undefined
  );

  const [warningMessage, setWarningMessage] = useState('');

  const { data: appointmentsMeta } = useAppointmentMeta();
  /** Actually the outcomes meta */
  const appointmentMeta = appointmentsMeta?.appt_outcomes;
  const pifuOutcomes = appointmentsMeta?.pifu_outcomes;
  const pifuPifuSelfBook = !!appointmentsMeta?.pifu_self_book;

  const [, setf2fComplete] = useSessionFormState('f2fComplete');
  const [postcode] = useSessionFormState('postcode');

  const [, setReason] = useSessionFormState('ReasonForReferral');
  const [, setSpeciality] = useSessionFormState('speciality');
  const [, setSubSpeciality] = useSessionFormState('subspeciality');
  const [, setRefClin] = useSessionFormState('refcli');
  const [, setF2fReferralPosted] = useSessionFormState('f2fReferralPosted');

  const [auth, setAuth] = useSessionFormState('auth', !!appointment?.auth_granted);
  const [authDate, setAuthDate] = useSessionFormState(
    'authDate',
    appointment?.auth_granted_start_date ? moment(appointment?.auth_granted_start_date) : undefined
  );
  const { appointmentVersion, outcomeFlags } = useSessionOutcome();

  const isRestricted = useIsRestricted();

  const { referral } = useApptReferral();

  useEffect(() => {
    if (user?.restricted) return;

    const referralWithClinicianCreated = referral?.ref_clinician;
    const selfBookReferralCreated = appointment?.referral_created && appointment?.auth_granted;

    if (referralWithClinicianCreated) {
      setf2fComplete(true);
      setSpeciality(undefined);
      setSubSpeciality(undefined);
      return;
    }

    if (selfBookReferralCreated) {
      setf2fComplete(true);
    }
  }, [
    appointment?.referral_created,
    appointment.auth_granted,
    isRestricted,
    referral,
    setSpeciality,
    setSubSpeciality,
    user?.restricted,
    setf2fComplete
  ]);

  useEffect(() => {
    if (!referral) return;

    if (referral?.ref_case_spec_cat_code) {
      setSpeciality(referral.ref_case_spec_cat_code);
    }

    if (referral?.ref_case_spec_area_code) {
      setSubSpeciality(referral.ref_case_spec_area_code);
    }

    if (referral?.ref_case_reason) {
      setReason(referral.ref_case_reason);
    }

    if (referral?.ref_clinician) {
      setRefClin({
        clin_user_guid: appointment.clinician_user_guid,
        hospital_id: referral.hospital_id || undefined,
        first_name: referral.ref_clinician,
        last_name: undefined,
        cat_name: referral.ref_case_spec_cat_desc,
        spec_areas: referral.ref_case_spec_area_desc,
        hospital_name: referral.hospital_name || undefined,
        postcode: postcode || referral.hospital_postcode || undefined
      });
    }
  }, [
    appointment.clinician_user_guid,
    dispatch,
    postcode,
    referral,
    setReason,
    setRefClin,
    setSpeciality,
    setSubSpeciality
  ]);

  const addSelectedAppointmentOutcome = useCallback(
    async (outcome: Parameters<typeof actions.appointmentOutcomeSave>[0]) => {
      const res = await dispatch(actions.appointmentOutcomeSave(outcome));
      if (res.type === 'CLINICIANS_SAVE_APPOINTMENT_OUTCOME_SUCCESS' && outcome.outcome_code) {
        setAppointmentOutcome(outcome.outcome_code);
      }
    },
    [dispatch, setAppointmentOutcome]
  );

  const onSelectAppointmentOutcome = useCallback(
    async (
      value?: string,
      authInfo?: {
        auth: boolean;
        authStart: Date | undefined;
      },
      overrideAuthAsFalse = false
    ) => {
      if (appointmentVersion !== 1) return;

      if (value) {
        // These fields were getting reset on refresh / leaving and rejoing appointment
        // Make sure we only erase these when we need to - outcome or auth has changed
        if (value !== appointmentOutcome || (authInfo && authInfo?.auth !== auth)) {
          setSpeciality(undefined);
          setSubSpeciality(undefined);
          setReason('');
        }

        const doesOutcomeRequireAuthToBeTrue = !!(
          outcomeFlags?.appt_auth_optional_flag &&
          outcomeFlags?.appt_auth_required_flag &&
          !overrideAuthAsFalse
        );

        const authStartDate =
          authInfo?.authStart || (doesOutcomeRequireAuthToBeTrue ? postedReferralStartDate : undefined);

        if (appointmentOutcome !== value) {
          setAuth(doesOutcomeRequireAuthToBeTrue);
          setAuthDate(moment(authStartDate) || undefined);
          setF2fReferralPosted(false);
          setPostedReferralStartDate(undefined);
          setf2fComplete(false);
        }
        if (!appointment?.appointment_completed) {
          const request = {
            user_guid: appointment?.clinician_user_guid,
            appt_id: appointment?.appointment_id,
            auth_granted: doesOutcomeRequireAuthToBeTrue || authInfo?.auth || false,
            auth_granted_start_date: authStartDate,
            outcome_code: value,
            rtt,
            priority
          };
          await addSelectedAppointmentOutcome(request);
        }

        onOutcomeSaved?.();
      }
    },
    [
      appointmentVersion,
      setSpeciality,
      setSubSpeciality,
      setReason,
      outcomeFlags,
      postedReferralStartDate,
      appointmentOutcome,
      appointment?.appointment_completed,
      appointment?.clinician_user_guid,
      appointment_id,
      onOutcomeSaved,
      setAuth,
      setAuthDate,
      setF2fReferralPosted,
      setPostedReferralStartDate,
      setf2fComplete,
      rtt,
      priority,
      addSelectedAppointmentOutcome
    ]
  );

  useEffect(() => {
    if (isCompleted) return;
    // If auth is undefined or null, fields haven't been initialised yet
    // Don't save yet as auth info could be lost
    if (auth === undefined || auth === null) return;

    // If the new appointment outcome doesn't match the saved one, then it has been changed (and triggered this useEffect)
    // If auth is true then this will need to be reset to false - rely on this state change to retrigger useEffect & save outcome
    // If auth is not true, then we want to save new outcome
    if (appointment.appointment_outcome_code !== appointmentOutcome && auth) {
      setAuth(false);
      setAuthDate(undefined);
      return;
    }

    onSelectAppointmentOutcome(appointmentOutcome, {
      auth,
      authStart: authDate?.toDate?.() || undefined
    });
  }, [appointmentOutcome, auth, authDate, isCompleted, onSelectAppointmentOutcome]);

  useEffect(() => {
    if (appointmentVersion !== 1) return;

    const outcomeRaisesTask = outcomeFlags?.appt_raised_task_flag;
    const selfBookSelected = auth;

    if (outcomeRaisesTask && !selfBookSelected) {
      setWarningMessage(`Task will be raised of type ${outcomeFlags?.appt_raised_task_description} once the appointment is
      completed.`);
    } else {
      setWarningMessage('');
    }
  }, [appointmentVersion, auth, outcomeFlags]);

  if (appointmentVersion !== 1) return null;

  return (
    <div data-private="1" data-testid="diagnosis-and-outcome-component" css={{ marginTop: 2 }}>
      <RecommendationsFormItem />
      <PriorityFormItem />
      <ReferralToTreatmentFormItem />
      <AppointmentOutcomeFormItem />

      {warningMessage && <Alert message={warningMessage} type="info" css={css({ marginBottom: '1rem' })} showIcon />}

      <SelfBookAndF2fReferralFields />
      <F2fCompletedFields onSelectAppointmentOutcome={onSelectAppointmentOutcome} />

      {outcomeFlags?.appt_pifu_flag && (
        <PIFUFields
          appointment={appointment}
          isCompleted={isCompleted}
          pifuOutcomes={pifuOutcomes}
          appointmentOutcome={appointmentOutcome}
          user={user}
          appointmentMeta={appointmentMeta}
          pifuPifuSelfBook={pifuPifuSelfBook}
        />
      )}

      <LetterTypesFormItem />
    </div>
  );
};

export default DiagnosisAndOutcome;
